import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import useBreakpoint from "@/src/lib/util/custom-hooks/useBreakPoint";
import Iconify from "@/src/components/iconify/Iconify";
import IconCode from "@/src/lib/iconCode";
import { CustomIconButton } from "@/src/lib/ui";

// Inline utilities
const renderArrow = (Comp, onClick, ariaLabel) => {
    if (!Comp) return null;
    if (React.isValidElement(Comp)) {
        return React.cloneElement(Comp, { onClick, "aria-label": ariaLabel });
    }
    if (typeof Comp === "function") {
        return <Comp onClick={onClick} aria-label={ariaLabel} />;
    }
    return null;
};

// Inline utilities and helpers
const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

function mergeResponsive(defaults, custom) {
    const base = { ...defaults, ...custom };
    const responsive = custom?.responsive ?? defaults?.responsive ?? [];
    return { ...base, responsive };
}

function applyBreakpoint(settings, windowWidth) {
    if (!settings.responsive || settings.responsive.length === 0)
        return settings;
    const sorted = [...settings.responsive].sort(
        (a, b) => a.breakpoint - b.breakpoint
    );
    let applied = { ...settings };
    for (const r of sorted) {
        if (windowWidth <= r.breakpoint) {
            applied = { ...applied, ...r.settings };
            if (
                applied.centerPadding != null &&
                typeof applied.centerPadding === "string"
            ) {
                const m = applied.centerPadding.match(/(-?\d+(?:\.\d+)?)px/);
                if (m) applied.centerPadding = parseFloat(m[1]);
            }
            break;
        }
    }
    return applied;
}

function useWindowWidth() {
    const [w, setW] = React.useState(
        typeof window !== "undefined" ? window.innerWidth : 1200
    );
    React.useEffect(() => {
        const onResize = () => setW(window.innerWidth);
        window.addEventListener("resize", onResize);
        return () => window.removeEventListener("resize", onResize);
    }, []);
    return w;
}

function useResizeObserver(ref) {
    const [rect, setRect] = React.useState({ width: 0, height: 0 });
    React.useEffect(() => {
        if (!ref.current || typeof ResizeObserver === "undefined") return;
        const el = ref.current;
        const ro = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const { width, height } = entry.contentRect;
                setRect({ width, height });
            }
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [ref]);
    return rect;
}

// Default arrows and settings
const CustomNextArrow = (props) => {
    const isMobile = useBreakpoint("(max-width: 640px)");
    const { className, style, onClick } = props || {};
    return (
        <div
            className={className}
            style={{
                ...style,
                position: "absolute",
                top: "40%",
                right: isMobile ? 8 : -20,
                transform: isMobile ? "translateX(0px)" : "translateX(40px)",
                zIndex: 3,
                cursor: "pointer",
            }}
            onClick={onClick}
        >
            <CustomIconButton
                aria-label="next arrow"
                color="inherit"
                className="relative"
                style={{
                    width: isMobile ? 40 : 50,
                    height: isMobile ? 40 : 50,
                    marginLeft: isMobile ? 0 : 50,
                    borderRadius: 9999,
                }}
            >
                <span
                    aria-hidden
                    className="absolute inset-0 rounded-full"
                    style={{ backgroundColor: "rgba(220, 38, 38, 0.2)" }}
                />
                <Iconify
                    icon={IconCode.arrowDown}
                    className="text-red-800 rotate-270 rounded-full"
                    width={isMobile ? 30 : 60}
                />
            </CustomIconButton>
        </div>
    );
};

const CustomPrevArrow = (props) => {
    const isMobile = useBreakpoint("(max-width: 640px)");
    const { className, style, onClick } = props || {};
    return (
        <div
            className={className}
            style={{
                ...style,
                position: "absolute",
                top: "40%",
                left: isMobile ? 8 : 0,
                transform: isMobile ? "translateX(0px)" : "translateX(-40px)",
                zIndex: 3,
                cursor: "pointer",
            }}
            onClick={onClick}
        >
            <CustomIconButton
                aria-label="prev arrow"
                color="inherit"
                className="relative"
                style={{
                    width: isMobile ? 40 : 50,
                    height: isMobile ? 40 : 50,
                    marginLeft: isMobile ? 0 : -25,
                    borderRadius: 9999,
                }}
            >
                <span
                    aria-hidden
                    className="absolute inset-0 rounded-full"
                    style={{ backgroundColor: "rgba(220, 38, 38, 0.2)" }}
                />
                <Iconify
                    icon={IconCode.arrowDown}
                    className="text-red-800 rotate-90 rounded-full"
                    width={isMobile ? 30 : 60}
                />
            </CustomIconButton>
        </div>
    );
};

function getDefaultSettings() {
    return {
        dots: false,
        infinite: false,
        autoplay: false,
        autoplaySpeed: 3000,
        speed: 500,
        slidesToShow: 4,
        slidesToScroll: 1,
        centerMode: false,
        centerPadding: 0,
        nextArrow: <CustomNextArrow />,
        prevArrow: <CustomPrevArrow />,
        responsive: [
            { breakpoint: 992, settings: { slidesToShow: 2 } },
            { breakpoint: 600, settings: { slidesToShow: 1 } },
        ],
    };
}

function useAutoplay({ autoplay, autoplaySpeed, realCount, next }) {
    const autoplayTimerRef = React.useRef(null);
    const stopAutoplay = React.useCallback(() => {
        if (autoplayTimerRef.current) {
            clearInterval(autoplayTimerRef.current);
            autoplayTimerRef.current = null;
        }
    }, []);
    const startAutoplay = React.useCallback(() => {
        if (!autoplay || realCount <= 1) return;
        stopAutoplay();
        autoplayTimerRef.current = setInterval(() => {
            next();
        }, Math.max(1000, autoplaySpeed));
    }, [autoplay, autoplaySpeed, realCount, next, stopAutoplay]);
    React.useEffect(() => {
        startAutoplay();
        return () => stopAutoplay();
    }, [startAutoplay, stopAutoplay]);
    return { autoplayTimerRef, startAutoplay, stopAutoplay };
}

function useDragEvents({
    dragging,
    dragStartX,
    slideWidthPx,
    slidesToScroll,
    moveTo,
    index,
    autoplay,
    startAutoplay,
    stopAutoplay,
    setDragging,
    setAnimating,
    setDragStartX,
    setDragDeltaX,
    suppressClickRef,
}) {
    const startedRef = React.useRef(false);
    const onPointerDown = React.useCallback(
        (e) => {
            if (e.pointerType === "mouse" && e.button !== 0) return;
            startedRef.current = true;
            setAnimating(false);
            setDragStartX(e.clientX);
            setDragDeltaX(0);
        },
        [setAnimating, setDragStartX, setDragDeltaX]
    );
    const onPointerMove = React.useCallback(
        (e) => {
            if (!startedRef.current) return;
            const delta = e.clientX - dragStartX;
            const abs = Math.abs(delta);
            if (!dragging && abs > 5) {
                setDragging(true);
                stopAutoplay();
                e.currentTarget.setPointerCapture?.(e.pointerId);
            }
            if (dragging) {
                setDragDeltaX(delta);
            }
        },
        [dragStartX, dragging, setDragging, stopAutoplay, setDragDeltaX]
    );
    const onPointerUp = React.useCallback(
        (e) => {
            if (!startedRef.current) return;
            const delta = e.clientX - dragStartX;
            const abs = Math.abs(delta);
            startedRef.current = false;
            if (!dragging) {
                setDragDeltaX(0);
                return;
            }
            const threshold = Math.min(80, slideWidthPx * 0.15);
            const velocity = Math.abs(e.velocityX ?? 0);
            let moved = 0;
            const isSwipe = abs > threshold || velocity > 0.4;
            if (isSwipe) {
                const approxSlides = Math.max(
                    1,
                    Math.round(abs / slideWidthPx)
                );
                moved = Math.min(
                    approxSlides * slidesToScroll,
                    Math.max(1, slidesToScroll)
                );
                if (delta < 0) moveTo(index + moved);
                else moveTo(index - moved);
                if (suppressClickRef) {
                    suppressClickRef.current = true;
                    setTimeout(() => {
                        if (suppressClickRef) suppressClickRef.current = false;
                    }, 250);
                }
            } else {
                moveTo(index);
            }
            setDragging(false);
            setDragDeltaX(0);
            if (autoplay) startAutoplay();
        },
        [
            dragStartX,
            dragging,
            slideWidthPx,
            slidesToScroll,
            moveTo,
            index,
            autoplay,
            startAutoplay,
            setDragging,
            setDragDeltaX,
        ]
    );
    const onTouchStart = React.useCallback(
        (e) => {
            if (!e.touches || e.touches.length === 0) return;
            startedRef.current = true;
            setAnimating(false);
            setDragStartX(e.touches[0].clientX);
            setDragDeltaX(0);
        },
        [setAnimating, setDragStartX, setDragDeltaX]
    );
    const onTouchMove = React.useCallback(
        (e) => {
            if (!startedRef.current) return;
            if (!e.touches || e.touches.length === 0) return;
            const delta = e.touches[0].clientX - dragStartX;
            const abs = Math.abs(delta);
            if (!dragging && abs > 5) {
                setDragging(true);
                stopAutoplay();
            }
            if (dragging) {
                setDragDeltaX(delta);
            }
        },
        [dragStartX, dragging, setDragging, stopAutoplay, setDragDeltaX]
    );
    const onTouchEnd = React.useCallback(
        (e) => {
            if (!startedRef.current) return;
            startedRef.current = false;
            const clientX =
                (e.changedTouches && e.changedTouches[0]?.clientX) ??
                dragStartX;
            const delta = clientX - dragStartX;
            const abs = Math.abs(delta);
            if (!dragging) {
                setDragDeltaX(0);
                return;
            }
            const threshold = Math.min(80, slideWidthPx * 0.15);
            let moved = 0;
            const isSwipe = abs > threshold;
            if (isSwipe) {
                const approxSlides = Math.max(
                    1,
                    Math.round(abs / slideWidthPx)
                );
                moved = Math.min(
                    approxSlides * slidesToScroll,
                    Math.max(1, slidesToScroll)
                );
                if (delta < 0) moveTo(index + moved);
                else moveTo(index - moved);
                if (suppressClickRef) {
                    suppressClickRef.current = true;
                    setTimeout(() => {
                        if (suppressClickRef) suppressClickRef.current = false;
                    }, 250);
                }
            } else {
                moveTo(index);
            }
            setDragging(false);
            setDragDeltaX(0);
            if (autoplay) startAutoplay();
        },
        [
            dragStartX,
            dragging,
            slideWidthPx,
            slidesToScroll,
            moveTo,
            index,
            autoplay,
            startAutoplay,
            setDragging,
            setDragDeltaX,
        ]
    );
    return {
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onTouchStart,
        onTouchMove,
        onTouchEnd,
    };
}

function useCarousel({ settings = {}, children }) {
    const isMobile = useBreakpoint("(max-width: 640px)");
    const containerRef = useRef(null);
    const trackRef = useRef(null);
    const defaultSettings = useMemo(() => getDefaultSettings(), []);
    const windowWidth = useWindowWidth();
    const merged = useMemo(
        () => mergeResponsive(defaultSettings, settings),
        [defaultSettings, settings]
    );
    const effective = useMemo(
        () => applyBreakpoint(merged, windowWidth),
        [merged, windowWidth]
    );
    const dots = !!effective.dots;
    const infinite = !!effective.infinite;
    const autoplay = !!effective.autoplay;
    const autoplaySpeed = Number(effective.autoplaySpeed ?? 3000);
    const speed = Number(effective.speed ?? 500);
    const slidesToShow = Math.max(1, Number(effective.slidesToShow ?? 1));
    let slidesToScroll = Math.max(1, Number(effective.slidesToScroll ?? 1));
    if (isMobile) slidesToScroll = 1;
    const centerMode = !!effective.centerMode;
    const centerPadding = Number(effective.centerPadding ?? 0);
    const childrenArray = useMemo(
        () => React.Children.toArray(children).filter(Boolean),
        [children]
    );
    const realCount = childrenArray.length;
    const _slidesToShow = centerMode ? 1 : slidesToShow;
    const cloneCount = infinite ? Math.max(_slidesToShow, slidesToScroll) : 0;
    const headClones = useMemo(
        () => (infinite ? childrenArray.slice(0, cloneCount) : []),
        [childrenArray, cloneCount, infinite]
    );
    const tailClones = useMemo(
        () => (infinite ? childrenArray.slice(-cloneCount) : []),
        [childrenArray, cloneCount, infinite]
    );
    const extendedSlides = useMemo(
        () => [...tailClones, ...childrenArray, ...headClones],
        [tailClones, childrenArray, headClones]
    );
    const [index, setIndex] = useState(() => (infinite ? cloneCount : 0));
    const [animating, setAnimating] = useState(false);
    const [dragging, setDragging] = useState(false);
    const [dragStartX, setDragStartX] = useState(0);
    const [dragDeltaX, setDragDeltaX] = useState(0);
    const suppressClickRef = useRef(false);
    useEffect(() => {
        setIndex(infinite ? cloneCount : 0);
    }, [
        realCount,
        cloneCount,
        infinite,
        _slidesToShow,
        slidesToScroll,
        centerMode,
    ]);
    const { width: containerWidth } = useResizeObserver(containerRef);
    const slideWidthPx = useMemo(() => {
        if (!containerWidth) return 0;
        if (centerMode) {
            const inner = clamp(
                containerWidth - 2 * centerPadding,
                0,
                Infinity
            );
            return inner;
        }
        return containerWidth / _slidesToShow;
    }, [containerWidth, _slidesToShow, centerMode, centerPadding]);
    const realIndex = useMemo(() => {
        if (!infinite)
            return clamp(index, 0, Math.max(0, realCount - _slidesToShow));
        const i = (index - cloneCount + realCount) % realCount;
        return i;
    }, [index, infinite, realCount, _slidesToShow, cloneCount]);
    const moveTo = useCallback(
        (target, { animate = true } = {}) => {
            if (slideWidthPx === 0) return;
            setAnimating(animate);
            setIndex(target);
        },
        [slideWidthPx]
    );
    const next = useCallback(() => {
        if (realCount === 0) return;
        if (infinite) {
            moveTo(index + slidesToScroll);
        } else {
            const maxIndex = Math.max(0, realCount - _slidesToShow);
            moveTo(clamp(index + slidesToScroll, 0, maxIndex));
        }
    }, [index, moveTo, infinite, slidesToScroll, realCount, _slidesToShow]);
    const prev = useCallback(() => {
        if (realCount === 0) return;
        if (infinite) {
            moveTo(index - slidesToScroll);
        } else {
            moveTo(Math.max(0, index - slidesToScroll));
        }
    }, [index, moveTo, infinite, slidesToScroll, realCount]);
    const pages = useMemo(() => {
        if (realCount <= _slidesToShow) return 1;
        return Math.ceil((realCount - _slidesToShow) / slidesToScroll) + 1;
    }, [realCount, _slidesToShow, slidesToScroll]);
    const activeDot = useMemo(() => {
        return clamp(Math.floor(realIndex / slidesToScroll), 0, pages - 1);
    }, [realIndex, slidesToScroll, pages]);
    const disablePrev = !infinite && index <= 0;
    const disableNext =
        !infinite && index >= Math.max(0, realCount - _slidesToShow);
    const useFullWidthMobile = isMobile && _slidesToShow === 1;
    // If there is only one real slide, center it within the container
    const singleOffset = useMemo(() => {
        if (realCount !== 1) return 0;
        if (!containerWidth || !slideWidthPx) return 0;
        if (centerMode) return 0;
        const stepWidth = useFullWidthMobile ? containerWidth : slideWidthPx;
        return (containerWidth - stepWidth) / 2;
    }, [realCount, containerWidth, slideWidthPx, centerMode, useFullWidthMobile]);
    const stepWidth = useFullWidthMobile ? containerWidth : slideWidthPx;
    const baseX = singleOffset - index * stepWidth;
    const dragX = dragging ? dragDeltaX : 0;
    const translateX = baseX + dragX;
    return {
        containerRef,
        trackRef,
        index,
        animating,
        dragging,
        dragStartX,
        dragDeltaX,
        setAnimating,
        setDragging,
        setDragStartX,
        setDragDeltaX,
        effective,
        extendedSlides,
        slideWidthPx,
        containerWidth,
        translateX,
        realIndex,
        pages,
        activeDot,
        disablePrev,
        disableNext,
        dots,
        infinite,
        autoplay,
        autoplaySpeed,
        speed,
        slidesToShow: _slidesToShow,
        slidesToScroll,
        centerMode,
        centerPadding,
        isMobile,
        realCount,
        cloneCount,
        moveTo,
        next,
        prev,
        setIndex,
        suppressClickRef,
    };
}

function Carousel({ settings: customSettings = {}, children }) {
    const carousel = useCarousel({ settings: customSettings, children });

    const {
        containerRef,
        trackRef,
        effective,
        extendedSlides,
        slideWidthPx,
        translateX,
        pages,
        activeDot,
        disablePrev,
        disableNext,
        dots,
        infinite,
        autoplay,
        autoplaySpeed,
        speed,
        slidesToShow,
        slidesToScroll,
        centerMode,
        centerPadding,
        isMobile,
        realCount,
        cloneCount,
        animating,
        dragging,
        dragStartX,
        dragDeltaX,
        index,
        setAnimating,
        setDragging,
        setDragStartX,
        setDragDeltaX,
        moveTo,
        next,
        prev,
    } = carousel;

    // Autoplay functionality
    const { startAutoplay, stopAutoplay } = useAutoplay({
        autoplay,
        autoplaySpeed,
        realCount,
        next,
    });

    // Drag events
    const {
        onPointerDown,
        onPointerMove,
        onPointerUp,
        onTouchStart,
        onTouchMove,
        onTouchEnd,
    } = useDragEvents({
        dragging,
        dragStartX,
        slideWidthPx,
        slidesToScroll,
        moveTo,
        index,
        autoplay,
        startAutoplay,
        stopAutoplay,
        setDragging,
        setAnimating,
        setDragStartX,
        setDragDeltaX,
        suppressClickRef: carousel.suppressClickRef,
    });

    // Handle transition end to snap for infinite
    const onTransitionEnd = useCallback(() => {
        if (!infinite || realCount === 0) return;
        let newIndex = index;
        if (index >= cloneCount + realCount) {
            // wrapped past the end -> snap back into range
            newIndex = cloneCount + ((index - cloneCount) % realCount);
            setAnimating(false);
            carousel.setIndex(newIndex);
        } else if (index < cloneCount) {
            const offset =
                (((index - cloneCount) % realCount) + realCount) % realCount;
            newIndex = cloneCount + offset;
            setAnimating(false);
            carousel.setIndex(newIndex);
        }
    }, [index, infinite, realCount, cloneCount, setAnimating, carousel]);

    // Keyboard support (Left/Right)
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const onKey = (e) => {
            if (e.key === "ArrowRight") next();
            if (e.key === "ArrowLeft") prev();
        };
        el.addEventListener("keydown", onKey);
        return () => el.removeEventListener("keydown", onKey);
    }, [next, prev, containerRef]);

    return (
        <div style={{ position: "relative" }}>
            {/* Main carousel container */}
            <div
                ref={containerRef}
                role="region"
                aria-roledescription="carousel"
                tabIndex={0}
                style={{
                    position: "relative",
                    overflow: "hidden",
                    paddingLeft: centerMode ? centerPadding : 0,
                    paddingRight: centerMode ? centerPadding : 0,
                    touchAction: "pan-y",
                    userSelect: dragging ? "none" : undefined,
                }}
                onMouseEnter={stopAutoplay}
                onMouseLeave={() => autoplay && startAutoplay()}
            >
                {/* Track */}
                <div
                    ref={trackRef}
                    onTransitionEnd={onTransitionEnd}
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    onClickCapture={(e) => {
                        if (carousel.suppressClickRef?.current) {
                            e.stopPropagation();
                            e.preventDefault();
                        }
                    }}
                    style={{
                        display: "flex",
                        willChange: "transform",
                        transform: `translate3d(${translateX}px, 0, 0)`,
                        transition: animating
                            ? `transform ${speed}ms ease`
                            : "none",
                        cursor: dragging ? "grabbing" : "grab",
                        touchAction: "pan-y",
                    }}
                >
                    {extendedSlides.map((child, i) => (
                        <div
                            key={`slide-${i}`}
                            style={{
                                flex:
                                    carousel.isMobile &&
                                    carousel.slidesToShow === 1
                                        ? "0 0 100%"
                                        : `0 0 ${carousel.slideWidthPx}px`,
                                width:
                                    carousel.isMobile &&
                                    carousel.slidesToShow === 1
                                        ? "100%"
                                        : `${carousel.slideWidthPx}px`,
                                boxSizing: "border-box",
                            }}
                        >
                            {child}
                        </div>
                    ))}
                </div>
            </div>

            {/* Arrows - Outside the overflow container (only show when enabled) */}
            {!disablePrev && (
                <div>
                    {renderArrow(effective.prevArrow, prev, "previous")}
                </div>
            )}
            {!disableNext && (
                <div>
                    {renderArrow(effective.nextArrow, next, "next")}
                </div>
            )}

            {/* Dots - Outside the overflow container */}
            {dots && pages > 1 && (
                <div
                    style={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 8,
                        display: "flex",
                        justifyContent: "center",
                        gap: 8,
                    }}
                >
                    {Array.from({ length: pages }).map((_, d) => (
                        <button
                            key={`dot-${d}`}
                            aria-label={`Go to slide ${d + 1}`}
                            onClick={() =>
                                moveTo(
                                    infinite
                                        ? cloneCount + d * slidesToScroll
                                        : d * slidesToScroll
                                )
                            }
                            style={{
                                width: 8,
                                height: 8,
                                borderRadius: 999,
                                border: 0,
                                padding: 0,
                                background: d === activeDot ? "#333" : "#bbb",
                                opacity: d === activeDot ? 1 : 0.6,
                                cursor: "pointer",
                            }}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default Carousel;
